import { Controller } from "@hotwired/stimulus"

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"]
const MAX_SIZE = 5 * 1024 * 1024

// Image upload for markdown editor.
// Supports drag & drop, paste, and file picker.
export default class extends Controller {
  static targets = ["textarea", "fileInput", "imageButton"]
  static values = {
    url: { type: String, default: "/dashboard/images" }
  }

  connect() {
    this.activeUploads = 0
    this.textareaTarget.addEventListener("paste", this.handlePaste)
    this.textareaTarget.addEventListener("drop", this.handleDrop)
    this.textareaTarget.addEventListener("dragover", this.handleDragOver)
    this.textareaTarget.addEventListener("dragleave", this.handleDragLeave)
  }

  disconnect() {
    this.textareaTarget.removeEventListener("paste", this.handlePaste)
    this.textareaTarget.removeEventListener("drop", this.handleDrop)
    this.textareaTarget.removeEventListener("dragover", this.handleDragOver)
    this.textareaTarget.removeEventListener("dragleave", this.handleDragLeave)
  }

  // Triggered by the toolbar image button
  selectFile() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  // Triggered by <input type="file"> change event
  handleFileSelect(event) {
    const files = event.target.files
    for (const file of files) {
      this.processFile(file)
    }
    // Reset so the same file can be re-selected
    event.target.value = ""
  }

  handlePaste = (event) => {
    const items = event.clipboardData?.items
    if (!items) return

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        event.preventDefault()
        this.processFile(item.getAsFile())
        break
      }
    }
  }

  handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    this.textareaTarget.classList.remove("drag-over")

    const files = event.dataTransfer?.files
    if (!files?.length) return

    for (const file of files) {
      if (file.type.startsWith("image/")) {
        this.processFile(file)
      }
    }
  }

  handleDragOver = (event) => {
    event.preventDefault()
    event.stopPropagation()
    this.textareaTarget.classList.add("drag-over")
  }

  handleDragLeave = (event) => {
    event.preventDefault()
    this.textareaTarget.classList.remove("drag-over")
  }

  // --- Private ---

  processFile(file) {
    if (!ALLOWED_TYPES.includes(file.type)) {
      this.showToast("Unsupported file type. Only JPG, PNG, GIF, and WebP are allowed.", "error")
      return
    }

    if (file.size > MAX_SIZE) {
      this.showToast("Image is too large. Maximum size is 5 MB.", "error")
      return
    }

    this.uploadImage(file)
  }

  uploadImage(file) {
    const ta = this.textareaTarget
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    const placeholder = `![Uploading ${file.name}…](${uploadId})`

    this.insertAtCursor(ta, placeholder)
    this.beginUpload()

    const formData = new FormData()
    formData.append("image[file]", file)

    const xhr = new XMLHttpRequest()
    xhr.open("POST", this.urlValue)
    xhr.setRequestHeader("X-CSRF-Token", document.querySelector('meta[name="csrf-token"]').content)
    xhr.setRequestHeader("Accept", "application/json")

    const placeholderPattern = this.buildPlaceholderRegex(uploadId)

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100)
        ta.value = ta.value.replace(placeholderPattern, `![Uploading ${pct}%…](${uploadId})`)
      }
    })

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText)
          const imageUrl = data.data?.url || data.url
          const markdown = `![${file.name}](${imageUrl})`
          ta.value = ta.value.replace(placeholderPattern, markdown)
          ta.dispatchEvent(new Event("input", { bubbles: true }))
        } catch {
          this.removePlaceholderById(ta, uploadId)
          this.showToast("Image upload failed. Please try again.", "error")
        }
      } else {
        this.removePlaceholderById(ta, uploadId)
        const errorMsg = this.parseXhrError(xhr)
        this.showToast(errorMsg, "error")
      }
      this.endUpload()
    })

    xhr.addEventListener("error", () => {
      this.removePlaceholderById(ta, uploadId)
      this.showToast("Image upload failed. Please try again.", "error")
      this.endUpload()
    })

    xhr.send(formData)
  }

  buildPlaceholderRegex(uploadId) {
    const escaped = uploadId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    return new RegExp(`!\\[[^\\]]*\\]\\(${escaped}\\)`)
  }

  removePlaceholderById(ta, uploadId) {
    const pattern = new RegExp(`!\\[[^\\]]*\\]\\(${uploadId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\)\\n?`)
    ta.value = ta.value.replace(pattern, "")
    ta.dispatchEvent(new Event("input", { bubbles: true }))
  }

  parseXhrError(xhr) {
    try {
      const json = JSON.parse(xhr.responseText)
      return json.error?.message || json.error || json.errors?.[0] || "Image upload failed. Please try again."
    } catch {
      return "Image upload failed. Please try again."
    }
  }

  insertAtCursor(textarea, text) {
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    textarea.setRangeText(text, start, end)
    textarea.selectionStart = textarea.selectionEnd = start + text.length
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
  }

  beginUpload() {
    this.activeUploads++
    if (this.hasImageButtonTarget) {
      this.imageButtonTarget.classList.add("is-uploading")
    }
  }

  endUpload() {
    this.activeUploads = Math.max(0, this.activeUploads - 1)
    if (this.activeUploads === 0 && this.hasImageButtonTarget) {
      this.imageButtonTarget.classList.remove("is-uploading")
    }
  }

  showToast(message, type = "error") {
    // Remove any existing upload toast
    document.querySelector(".toast[data-source='image-upload']")?.remove()

    const toast = document.createElement("div")
    toast.className = `toast toast-${type}`
    toast.setAttribute("data-controller", "toast")
    toast.setAttribute("data-source", "image-upload")
    toast.innerHTML = `
      <span>${this.escapeHtml(message)}</span>
      <button class="toast-close" data-action="toast#dismiss">&times;</button>
    `
    document.body.appendChild(toast)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
